import { Plan } from '@accedo/one-plugin-sdk';
import { ReactNode } from 'react';

import {
  FormInputTypes,
  InputApiField,
  TFormData,
  TFormResponse,
} from '#/client';
import { FormInputVariants } from '#/service/constants';
import { Dictionary, PartialDictionary } from '#/service/utils';

export enum InputGroupType {
  Field = 'field',
  Plan = 'plan',
  Button = 'button',
}

export enum FormAction {
  Update = 'update',
  Error = 'error',
  Reset = 'reset',
  PlanChange = 'planChange',
}

export interface InputBase {
  key: string;
  type: FormInputTypes;
}

export interface InputVariantBase extends InputBase {
  type: FormInputTypes.INPUT | FormInputTypes.STATIC;
  variant: FormInputVariants;
}

export interface InputPlanBase extends InputBase {
  type: FormInputTypes.PLAN | FormInputTypes.STATIC;
  id: string;
}

export interface InputField extends InputVariantBase, InputApiField {
  variant: Exclude<
    FormInputVariants,
    FormInputVariants.BUTTON | FormInputVariants.SUBMIT
  >;
  defaultValue?: string;
  description?: string;
  validations?: TValidation[];
  help?: ReactNode;
  format?: string;
}

export interface InputPlan extends Plan, InputPlanBase {
  isDisabled?: boolean;
}

export interface InputButtonBase extends InputVariantBase {
  text: string;
  onClick: Function;
  disclaimer?: string;
}

export interface InputButtonRegular extends InputButtonBase {
  variant: FormInputVariants.BUTTON;
  onClick: (formData: TFormData) => void;
  isMainButton?: boolean;
}

export interface InputButtonSubmit extends InputButtonBase {
  variant: FormInputVariants.SUBMIT;
  onClick: (formData: TFormData) => Promise<TFormResponse>;
  isMainButton?: false;
}

export type TValidation = {
  message?: string;
  regexp?: string;
  match?: string;
  remote?: boolean;
};

export type TInputState<T = string | boolean> = {
  touched?: boolean;
  value?: T;
};

export type InputGroup = InputFieldGroup | InputPlanGroup | InputButtonGroup;

export type TInputGroup<
  TInputGroupType extends InputGroupType,
  TInputs extends any[],
> = {
  key: string;
  type: TInputGroupType;
  inputs: TInputs;
};

export type InputFieldGroup = TInputGroup<InputGroupType.Field, InputField[]>;

export type InputPlanGroup = TInputGroup<InputGroupType.Plan, InputPlan[]> & {
  selectedPlanId?: string;
};

export type InputButtonGroup = TInputGroup<
  InputGroupType.Button,
  InputButton[]
>;

export type InputButton = InputButtonRegular | InputButtonSubmit;

export type FormState = {
  inputs: Dictionary<string, TInputState>;
  errorMessages: PartialDictionary<string, string[]>;
};

export type Action = {
  type: string;
  payload?: any; // TODO any
};

export interface ValidationListType {
  key: string;
  groupKey: string;
  validations: TValidation[];
}

export interface FormProps {
  inputGroups: InputGroup[];

  // TODO#registrationHackMode: only for registration. remove later.
  /**
   * Temporary hack (3+ years old) that makes the form working nicely with the Register flow.
   * When you enable it, the form won't be cleared on the submit. This is especially important
   * when form has two-step submit. Without it user would observe a strange quirk that his form
   * get cleared after the first step.
   * @default false
   * */
  registrationHackMode?: boolean;

  // TODO#wideForm: only for "update subscription". need to revisit later.
  isWide?: boolean;
}

// TODO#wideForm: the wide option is only needed because of the "update subscription" form: zpl.io/2jD3oYO
// revisit this later because Rasmus might change the design.
export interface WideFormProps {
  $isWideForm?: boolean;
}

export interface StyledInputProps {
  $hasLeftIcon?: boolean;
  $hasRightIcon?: boolean;
  $highlighted: boolean;
  $touched?: boolean;
  errorMessage?: string;
}

export interface StyledTextAreaLabel {
  $hide: boolean;
  $highlighted: boolean;
}

export type SubmitButtonGroupParams = {
  submit: {
    textKeyPath: string;
    onClick: (data: TFormData) => Promise<TFormResponse>;
    disclaimer?: string;
  };
  cancel?: {
    textKeyPath?: string;
    onClick: () => void;
  };
  hasSubmitButton: true;
};

export type MainButtonGroupParams = {
  main: {
    textKeyPath: string;
    onClick: (data: TFormData) => void;
    disclaimer?: string;
  };
  cancel?: {
    textKeyPath?: string;
    onClick: () => void;
  };
  hasSubmitButton: false;
};

export type FormGroupButtonProps = {
  isButtonDisabled: (button: InputButton) => boolean;
  activeButtonKey?: string;
  button: InputButton;
  handleSubmit: Function;
  getFormData: Function;
};
